import { useCallback, useState } from 'react';
import type { UseQueryOptions } from '@tanstack/react-query';

import useGqlQuery, { type CustomConfig } from './useGqlQuery';

const useLazyGqlQuery = <TData = unknown, TVariables = Record<string, unknown>>(
  query: string,
  config: Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn'> & CustomConfig,
) => {
  const [variables, setVariables] = useState<TVariables | null>(null);

  const queryData = useGqlQuery<TData>(query, {
    ...config,
    variables: (variables ?? undefined) as Record<string, unknown> | undefined,
    queryKey: config.queryKey.concat(variables),
    enabled: variables != null,
  });

  const trigger = useCallback((vars: TVariables) => {
    setVariables(vars);
  }, []);

  const returnedQuery = import.meta.env.DEV
    ? (new Proxy(queryData, {
        get(target, prop) {
          if (prop === 'loading') {
            throw new Error('`loading` flag is deprecated, please use `isLoading`');
          }

          return target[prop as keyof typeof target];
        },
      }) as typeof queryData)
    : queryData;

  return [trigger, returnedQuery] as const;
};

export default useLazyGqlQuery;
